import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import RichTextEditor from "@/components/RichTextEditor";
import { ArrowLeft, Save } from "lucide-react";

const CATEGORY_OPTIONS = [
    { value: "estate-planning", label: "Estate Planning" },
    { value: "elder-law", label: "Elder Law" },
    { value: "real-estate", label: "Real Estate" },
    { value: "business-law", label: "Business Law" },
    { value: "news", label: "News" },
];

const EMPTY = {
    slug: "",
    title: "",
    excerpt: "",
    category: "estate-planning",
    date: new Date().toISOString().slice(0, 10),
    read_time: "5 min read",
    image: "",
    content_html: "",
    published: true,
};

function slugify(s) {
    return (s || "")
        .toLowerCase()
        .trim()
        .replace(/['’]/g, "")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 120);
}

export default function AdminEditor() {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEdit = Boolean(id);
    const [form, setForm] = useState(EMPTY);
    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    const [slugTouched, setSlugTouched] = useState(false);

    useEffect(() => {
        document.title = isEdit ? "Edit post | Chesterton Admin" : "New post | Chesterton Admin";
    }, [isEdit]);

    useEffect(() => {
        if (!isEdit) return;
        (async () => {
            try {
                const { data } = await api.get(`/admin/posts/${id}`);
                setForm({
                    slug: data.slug || "",
                    title: data.title || "",
                    excerpt: data.excerpt || "",
                    category: data.category || "estate-planning",
                    date: (data.date || "").slice(0, 10) || EMPTY.date,
                    read_time: data.read_time || "5 min read",
                    image: data.image || "",
                    content_html: data.content_html || "",
                    published: data.published !== false,
                });
                setSlugTouched(true);
            } catch (err) {
                toast.error(formatApiError(err));
                navigate("/admin", { replace: true });
            } finally {
                setLoading(false);
            }
        })();
    }, [id, isEdit, navigate]);

    const update = (k) => (v) => {
        const value = v?.target ? v.target.value : v;
        setForm((f) => ({ ...f, [k]: value }));
    };

    const onTitleChange = (e) => {
        const title = e.target.value;
        setForm((f) => ({
            ...f,
            title,
            slug: !isEdit && !slugTouched ? slugify(title) : f.slug,
        }));
    };

    const onSlugChange = (e) => {
        setSlugTouched(true);
        setForm((f) => ({ ...f, slug: slugify(e.target.value) }));
    };

    const validate = () => {
        if (!form.title.trim()) return "Title is required.";
        if (!form.slug.trim()) return "Slug is required.";
        if (!form.excerpt.trim()) return "Excerpt is required.";
        if (!form.content_html || form.content_html === "<p></p>") return "Add some content to the post.";
        return null;
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        const err = validate();
        if (err) {
            toast.error(err);
            return;
        }
        setSaving(true);
        try {
            if (isEdit) {
                await api.put(`/admin/posts/${id}`, form);
                toast.success("Post updated.");
            } else {
                await api.post("/admin/posts", form);
                toast.success("Post created.");
            }
            navigate("/admin", { replace: true });
        } catch (er) {
            toast.error(formatApiError(er));
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-16 text-acre-muted text-sm">Loading…</div>;
    }

    return (
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-8" data-testid={isEdit ? "admin-edit-page" : "admin-new-page"}>
            <Link
                to="/admin"
                data-testid="admin-back"
                className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-acre-muted hover:text-acre-ink mb-6"
            >
                <ArrowLeft size={13} /> All posts
            </Link>

            <form onSubmit={onSubmit} data-testid="admin-post-form" className="space-y-8">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                    <div className="lg:col-span-8 space-y-6">
                        <div className="space-y-2">
                            <Label htmlFor="title" className="text-xs uppercase tracking-[0.18em] text-acre-muted">Title *</Label>
                            <Input
                                id="title"
                                data-testid="admin-input-title"
                                value={form.title}
                                onChange={onTitleChange}
                                placeholder="Post title"
                                className="rounded-none border-acre-line focus-visible:ring-acre-primary text-xl py-6"
                                required
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="excerpt" className="text-xs uppercase tracking-[0.18em] text-acre-muted">Excerpt *</Label>
                            <Textarea
                                id="excerpt"
                                data-testid="admin-input-excerpt"
                                value={form.excerpt}
                                onChange={update("excerpt")}
                                rows={3}
                                placeholder="One or two sentences that appear on the blog index and in previews."
                                className="rounded-none border-acre-line focus-visible:ring-acre-primary"
                                required
                            />
                        </div>

                        <div className="space-y-2">
                            <Label className="text-xs uppercase tracking-[0.18em] text-acre-muted">Content *</Label>
                            <RichTextEditor
                                value={form.content_html}
                                onChange={update("content_html")}
                            />
                        </div>
                    </div>

                    <aside className="lg:col-span-4 space-y-6">
                        <div className="bg-white border border-acre-line p-6 space-y-5">
                            <div className="space-y-2">
                                <Label htmlFor="slug" className="text-xs uppercase tracking-[0.18em] text-acre-muted">Slug *</Label>
                                <Input
                                    id="slug"
                                    data-testid="admin-input-slug"
                                    value={form.slug}
                                    onChange={onSlugChange}
                                    className="rounded-none border-acre-line focus-visible:ring-acre-primary font-mono text-sm"
                                    required
                                />
                                <p className="text-[11px] text-acre-muted">/blog/{form.slug || "your-slug"}</p>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="category" className="text-xs uppercase tracking-[0.18em] text-acre-muted">Category *</Label>
                                <Select value={form.category} onValueChange={update("category")}>
                                    <SelectTrigger
                                        id="category"
                                        data-testid="admin-select-category"
                                        className="rounded-none border-acre-line focus:ring-acre-primary"
                                    >
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {CATEGORY_OPTIONS.map((c) => (
                                            <SelectItem
                                                key={c.value}
                                                value={c.value}
                                                data-testid={`admin-option-${c.value}`}
                                            >
                                                {c.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="date" className="text-xs uppercase tracking-[0.18em] text-acre-muted">Date *</Label>
                                    <Input
                                        id="date"
                                        type="date"
                                        data-testid="admin-input-date"
                                        value={form.date}
                                        onChange={update("date")}
                                        className="rounded-none border-acre-line focus-visible:ring-acre-primary"
                                        required
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="read_time" className="text-xs uppercase tracking-[0.18em] text-acre-muted">Read time</Label>
                                    <Input
                                        id="read_time"
                                        data-testid="admin-input-readtime"
                                        value={form.read_time}
                                        onChange={update("read_time")}
                                        placeholder="5 min read"
                                        className="rounded-none border-acre-line focus-visible:ring-acre-primary"
                                    />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="image" className="text-xs uppercase tracking-[0.18em] text-acre-muted">Cover image URL <span className="normal-case tracking-normal text-acre-muted/70">(optional)</span></Label>
                                <Input
                                    id="image"
                                    data-testid="admin-input-image"
                                    value={form.image}
                                    onChange={update("image")}
                                    placeholder="Leave blank for a default image"
                                    className="rounded-none border-acre-line focus-visible:ring-acre-primary text-sm"
                                />
                                <p className="text-[11px] text-acre-muted">If left blank, we’ll set a tasteful default that fits the category.</p>
                                {form.image && (
                                    <div className="mt-2 aspect-[4/3] bg-acre-line overflow-hidden">
                                        <img
                                            src={form.image}
                                            alt=""
                                            className="w-full h-full object-cover"
                                            onError={(e) => { e.currentTarget.style.opacity = 0.2; }}
                                        />
                                    </div>
                                )}
                            </div>
                            <div className="flex items-center justify-between pt-2 border-t border-acre-line">
                                <div>
                                    <Label htmlFor="published" className="text-xs uppercase tracking-[0.18em] text-acre-muted">Published</Label>
                                    <p className="text-[11px] text-acre-muted">Off = draft, hidden from public.</p>
                                </div>
                                <Switch
                                    id="published"
                                    data-testid="admin-toggle-published"
                                    checked={form.published}
                                    onCheckedChange={update("published")}
                                />
                            </div>
                        </div>

                        <Button
                            type="submit"
                            disabled={saving}
                            data-testid="admin-save-button"
                            className="w-full rounded-none bg-acre-primary hover:bg-acre-primaryHover text-white py-6 text-sm tracking-wide"
                        >
                            {saving ? "Saving…" : (<><Save size={16} className="mr-2" /> {isEdit ? "Save changes" : "Publish post"}</>)}
                        </Button>
                    </aside>
                </div>
            </form>
        </div>
    );
}
